/**
 * Детерминированный визуал категории.
 *
 * Категория не хранит картинку: цвет и глиф выводятся из имени. Одинаковое
 * имя всегда даёт одинаковый визуал на всех устройствах, без сети и файлов.
 */

export interface CategoryGradient {
  /** Начальный цвет градиента. */
  from: string
  /** Конечный цвет градиента. */
  to: string
}

/**
 * 24 подобранных градиента: различимы на белом и в тёмной теме,
 * без «кислотных» сочетаний.
 */
export const CATEGORY_PALETTE: CategoryGradient[] = [
  { from: '#FF6B6B', to: '#C92A2A' },
  { from: '#FF922B', to: '#D9480F' },
  { from: '#FCC419', to: '#E67700' },
  { from: '#94D82D', to: '#5C940D' },
  { from: '#51CF66', to: '#2B8A3E' },
  { from: '#20C997', to: '#087F5B' },
  { from: '#22B8CF', to: '#0B7285' },
  { from: '#4DABF7', to: '#1864AB' },
  { from: '#748FFC', to: '#364FC7' },
  { from: '#9775FA', to: '#5F3DC4' },
  { from: '#DA77F2', to: '#862E9C' },
  { from: '#F783AC', to: '#A61E4D' },
  { from: '#FFA94D', to: '#E8590C' },
  { from: '#A9E34B', to: '#66A80F' },
  { from: '#38D9A9', to: '#0CA678' },
  { from: '#66D9E8', to: '#1098AD' },
  { from: '#74C0FC', to: '#1971C2' },
  { from: '#B197FC', to: '#6741D9' },
  { from: '#E599F7', to: '#9C36B5' },
  { from: '#FAA2C1', to: '#C2255C' },
  { from: '#FFC078', to: '#D9480F' },
  { from: '#8CE99A', to: '#2F9E44' },
  { from: '#99E9F2', to: '#0C8599' },
  { from: '#D0BFFF', to: '#5F3DC4' }
]

/** Количество минималистичных глифов в наборе. */
export const CATEGORY_GLYPH_COUNT = 16

export interface CategoryVisual {
  /** Индекс градиента в палитре. */
  colorKey: number
  /** Индекс глифа. */
  glyph: number
}

/** FNV-1a: стабильный 32-битный хеш строки. */
export function hashString(value: string): number {
  let hash = 0x811c9dc5
  for (let i = 0; i < value.length; i++) {
    hash ^= value.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193)
  }
  return hash >>> 0
}

/**
 * Визуал категории. Ручные `colorKey`/`glyph` имеют приоритет над хешем.
 */
export function categoryVisual(
  name: string,
  colorKey?: number | null,
  glyph?: number | null
): CategoryVisual {
  const hash = hashString(name.trim().toLowerCase())
  return {
    colorKey:
      colorKey != null && colorKey >= 0
        ? colorKey % CATEGORY_PALETTE.length
        : hash % CATEGORY_PALETTE.length,
    glyph:
      glyph != null && glyph >= 0
        ? glyph % CATEGORY_GLYPH_COUNT
        : Math.floor(hash / CATEGORY_PALETTE.length) % CATEGORY_GLYPH_COUNT
  }
}

/** Градиент категории (для плиток товаров и фонов). */
export function categoryGradient(
  name: string,
  colorKey?: number | null
): CategoryGradient {
  return CATEGORY_PALETTE[categoryVisual(name, colorKey).colorKey]
}
